import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useCQuery, invalidateQuery, FC } from '../../Services'
import { keepPreviousData } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import LinearProgress from '@mui/material/LinearProgress'
import Alert from '@mui/material/Alert'
import ListAltIcon from '@mui/icons-material/ListAlt'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import SearchIcon from '@mui/icons-material/Search'
import { ErrorDialog } from './ErrorDialog'
import { ProcessesTable } from './ProcessesTable'
import { ProcessesFilters } from './ProcessesFilters'

const initialDates = [
  DateTime.now().startOf('day').toJSDate(),
  DateTime.now().endOf('day').toJSDate()
]

export const Processes = () => {
  const [dateRange, setDateRange] = useState(initialDates)
  const [filtersOnQuery, setFiltersOnQuery] = useState({
    createdAt: { $gte: initialDates[0], $lte: initialDates[1] }
  })
  const [contentSearch, setContentSearch] = useState('')
  const [onlyErrors, setOnlyErrors] = useState(false)
  const [onlyDuplicates, setOnlyDuplicates] = useState(false)
  const [agencyFilterId, setAgencyFilterId] = useState(null)
  const [modalData, setModalData] = useState(null)
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false)
  const [selectedRows, setSelectedRows] = useState([])
  const [hasSearched, setHasSearched] = useState(false)

  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 })
  const [docTypeFilter, setDocTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [fileNameFilter, setFileNameFilter] = useState('')
  const [debouncedFileNameFilter, setDebouncedFileNameFilter] = useState('')
  const [hashFilter, setHashFilter] = useState('')
  const [debouncedHashFilter, setDebouncedHashFilter] = useState('')

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedFileNameFilter(fileNameFilter), 400)
    return () => clearTimeout(handler)
  }, [fileNameFilter])

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedHashFilter(hashFilter), 400)
    return () => clearTimeout(handler)
  }, [hashFilter])

  useEffect(() => {
    setPaginationModel(prev => prev.page === 0 ? prev : { ...prev, page: 0 })
  }, [debouncedFileNameFilter, debouncedHashFilter])

  const loggedInUser = JSON.parse(window.localStorage.getItem('user')).user
  const isAdmin = ['siapAdmin', 'siapDeveloper'].includes(loggedInUser?.role)
  const isQueryEnabled = isAdmin ? hasSearched : true

  const effectiveFilters = useMemo(() => {
    const filters = { ...filtersOnQuery }
    if (agencyFilterId) filters.agencyId = agencyFilterId
    if (onlyErrors) filters.$or = [{ 'retryErrors.0': { $exists: true } }, { ftpError: { $ne: null } }, { status: 'error' }]
    if (onlyDuplicates) filters.isDuplicate = true
    return filters
  }, [filtersOnQuery, agencyFilterId, onlyErrors, onlyDuplicates])

  const serverQuery = useMemo(() => ({
    $skip: paginationModel.page * paginationModel.pageSize,
    $limit: paginationModel.pageSize,
    $sort: { createdAt: -1 },
    ...effectiveFilters,
    ...(docTypeFilter ? { docType: docTypeFilter } : {}),
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(debouncedFileNameFilter ? { fileName: debouncedFileNameFilter } : {}),
    ...(debouncedHashFilter ? { fileHash: debouncedHashFilter } : {})
  }), [paginationModel, effectiveFilters, docTypeFilter, statusFilter, debouncedFileNameFilter, debouncedHashFilter])

  const hasContentSearch = Boolean(filtersOnQuery.contentSearch)

  const { data: processesData, isSuccess: isSuccessProcesses, isFetching, isError, error } = useCQuery(
    ['processesPage', serverQuery],
    [],
    {
      enabled: isQueryEnabled,
      placeholderData: keepPreviousData,
      retry: hasContentSearch ? 0 : 1
    }
  )
  const processes = processesData?.data || []
  const totalRows = processesData?.total || 0
  const totalIsEstimate = processesData?.totalIsEstimate || false

  const errorCountQuery = useMemo(() => {
    const q = { ...filtersOnQuery }
    if (agencyFilterId) q.agencyId = agencyFilterId
    if (docTypeFilter) q.docType = docTypeFilter
    if (statusFilter) q.status = statusFilter
    if (debouncedFileNameFilter) q.fileName = debouncedFileNameFilter
    if (debouncedHashFilter) q.fileHash = debouncedHashFilter
    return q
  }, [filtersOnQuery, agencyFilterId, docTypeFilter, statusFilter, debouncedFileNameFilter, debouncedHashFilter])

  const { data: errorCount = 0 } = useCQuery(
    ['processesErrorCount', errorCountQuery],
    [],
    { enabled: isQueryEnabled }
  )

  const { data: agencies = [], isSuccess: isSuccessAgencies } = useCQuery('agencies')
  const { data: clients = [], isSuccess: isSuccessClients } = useCQuery('clients')

  const handleSearch = useCallback(() => {
    const daysDiff = Math.ceil(
      DateTime.fromJSDate(dateRange[1])
        .diff(DateTime.fromJSDate(dateRange[0]), 'days').values.days
    )

    if (contentSearch && daysDiff > 8) {
      window.growl.show({
        severity: 'error',
        summary: 'Errore',
        detail: 'Per la ricerca sul contenuto del file il range massimo è di 7 giorni'
      })
      return
    }

    setFiltersOnQuery({
      createdAt: { $gte: dateRange[0], $lte: dateRange[1] },
      ...(contentSearch ? { contentSearch } : {})
    })
    setSelectedRows([])
    setPaginationModel(prev => ({ ...prev, page: 0 }))
    if (!hasSearched) setHasSearched(true)
  }, [dateRange, contentSearch, hasSearched])

  const handleRefresh = useCallback(() => {
    invalidateQuery(['processesPage', 'processesErrorCount'])
  }, [])

  const handleDeleteProcess = useCallback(async (processId) => {
    await FC.service('processes').remove(processId)
    invalidateQuery(['processesPage', 'processesErrorCount'])
  }, [])

  const handleOpenErrorDialog = useCallback((row) => {
    setModalData(row)
    setIsErrorDialogOpen(true)
  }, [])

  const handleCloseErrorDialog = useCallback(() => {
    setIsErrorDialogOpen(false)
  }, [])

  const handleDocTypeFilterChange = useCallback((value) => {
    setDocTypeFilter(value)
    setPaginationModel(prev => ({ ...prev, page: 0 }))
  }, [])

  const handleStatusFilterChange = useCallback((value) => {
    setStatusFilter(value)
    setPaginationModel(prev => ({ ...prev, page: 0 }))
  }, [])

  const handleFileNameFilterChange = useCallback((value) => {
    setFileNameFilter(value)
  }, [])

  const handleHashFilterChange = useCallback((value) => {
    setHashFilter(value)
  }, [])

  const handleAgencyFilterChange = useCallback((value) => {
    setAgencyFilterId(value)
    setPaginationModel(prev => ({ ...prev, page: 0 }))
  }, [])

  const handleToggleOnlyErrors = useCallback(() => {
    setOnlyErrors(prev => !prev)
    setPaginationModel(prev => ({ ...prev, page: 0 }))
  }, [])

  const handleToggleOnlyDuplicates = useCallback(() => {
    setOnlyDuplicates(prev => !prev)
    setPaginationModel(prev => ({ ...prev, page: 0 }))
  }, [])

  if (!isSuccessAgencies || !isSuccessClients || (!isAdmin && !isSuccessProcesses)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Stack alignItems='center' spacing={2}>
          <CircularProgress size={40} />
          <Typography variant='body2' color='text.secondary'>
            Caricamento processi...
          </Typography>
        </Stack>
      </Box>
    )
  }

  return (
    <Box sx={{ mx: 'auto', width: '94%', maxWidth: 1600, pb: 5 }}>
      <ErrorDialog
        open={isErrorDialogOpen}
        onClose={handleCloseErrorDialog}
        process={modalData}
        isAdmin={isAdmin}
      />

      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        sx={{ mb: 2, mt: 1 }}
      >
        <Stack direction='row' alignItems='center' spacing={2}>
          <ListAltIcon color='primary' sx={{ fontSize: 28 }} />
          <Typography variant='h5' fontWeight={700} color='text.primary'>
            Processi
          </Typography>
          {isQueryEnabled && isSuccessProcesses && (
            <Chip
              label={totalIsEstimate ? `${totalRows}+ risultati` : `${totalRows} risultati`}
              size='small'
              variant='outlined'
              sx={{ fontWeight: 500 }}
            />
          )}
          {isQueryEnabled && errorCount > 0 && (
            <Chip
              icon={<ErrorOutlineIcon />}
              label={onlyErrors ? `Solo errori (${errorCount})` : `${errorCount} con errori`}
              size='small'
              color='error'
              variant={onlyErrors ? 'filled' : 'outlined'}
              onClick={handleToggleOnlyErrors}
              onDelete={onlyErrors ? handleToggleOnlyErrors : undefined}
              sx={{ fontWeight: 600, cursor: 'pointer' }}
            />
          )}
          {isQueryEnabled && onlyErrors && errorCount === 0 && (
            <Chip
              icon={<ErrorOutlineIcon />}
              label='Solo errori (0)'
              size='small'
              color='error'
              variant='filled'
              onClick={handleToggleOnlyErrors}
              onDelete={handleToggleOnlyErrors}
              sx={{ fontWeight: 600, cursor: 'pointer' }}
            />
          )}
        </Stack>
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2
        }}
      >
        <ProcessesFilters
          dateRange={dateRange}
          setDateRange={setDateRange}
          contentSearch={contentSearch}
          setContentSearch={setContentSearch}
          onlyErrors={onlyErrors}
          setOnlyErrors={setOnlyErrors}
          onlyDuplicates={onlyDuplicates}
          onToggleOnlyDuplicates={handleToggleOnlyDuplicates}
          onSearch={handleSearch}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          agencies={agencies}
          processes={processes}
          agencyFilterId={agencyFilterId}
          setAgencyFilterId={handleAgencyFilterChange}
        />
      </Paper>

      {!isQueryEnabled && (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            textAlign: 'center'
          }}
        >
          <SearchIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
          <Typography variant='h6' color='text.secondary' fontWeight={600}>
            Imposta i filtri e avvia la ricerca
          </Typography>
          <Typography variant='body2' color='text.disabled' sx={{ mt: 0.5 }}>
            Seleziona un periodo, un'agenzia o altri parametri, poi clicca "Ricerca" per caricare i processi.
          </Typography>
        </Paper>
      )}

      {isQueryEnabled && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2
          }}
        >
          {isError
            ? (
              <Alert severity='error' sx={{ mb: 2 }}>
                Ricerca non completata{error?.message ? `: ${error.message}` : ''}.
                {hasContentSearch
                  ? ' Riduci il periodo (max 7 giorni) o restringi i filtri e riprova.'
                  : ' Riprova tra qualche istante.'}
              </Alert>
              )
            : null}
          {!isSuccessProcesses && !isError
            ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <Stack alignItems='center' spacing={2}>
                  <CircularProgress size={36} />
                  <Typography variant='body2' color='text.secondary'>
                    Caricamento processi...
                  </Typography>
                </Stack>
              </Box>
              )
            : isSuccessProcesses
              ? (
              <>
                {isFetching && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}
                <ProcessesTable
                  processes={processes}
                  totalRows={totalRows}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  docTypeFilter={docTypeFilter}
                  onDocTypeFilterChange={handleDocTypeFilterChange}
                  statusFilter={statusFilter}
                  onStatusFilterChange={handleStatusFilterChange}
                  fileNameFilter={fileNameFilter}
                  onFileNameFilterChange={handleFileNameFilterChange}
                  hashFilter={hashFilter}
                  onHashFilterChange={handleHashFilterChange}
                  agencies={agencies}
                  clients={clients}
                  isAdmin={isAdmin}
                  openModal={handleOpenErrorDialog}
                  selectedRows={selectedRows}
                  setSelectedRows={setSelectedRows}
                  onRefresh={handleRefresh}
                  onDeleteProcess={loggedInUser?.role === 'siapAdmin' ? handleDeleteProcess : undefined}
                />
              </>
              )
              : null}
        </Paper>
      )}
    </Box>
  )
}
